import {
  Appointment,
  CareTeam,
  Coding,
  Device,
  Extension,
  MedicationRequest,
  Practitioner,
  PractitionerRole,
  QuestionnaireResponse,
  Reference,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes';
import { APPOINTMENT_TYPES_SYSTEM } from '@bariendo/lib/constants';
import {
  DOSESPOT_STATUS_EXTENSION,
  EARLIEST_WINS_MILESTONES,
  HEALTHIE_DATA_EXTENSION,
  HEALTHIE_LAB_REVIEW_FORM_NAMES,
  LAB_REVIEW_QUESTIONNAIRES,
  MILESTONE_CODES,
  MilestoneCode,
  PREPROCEDURE_APPOINTMENT_CODES,
  PREPROCEDURE_TASK_CODES,
  PreprocedureTaskCode,
  PROCEDURE_DISPLAY,
} from './constants';
import { LabSummary, LabsReviewed, Milestones, ProcedureAppointment, RxSummary, TasksByCode } from './types';

/** `Patient/abc` → `abc`, only when the reference is of the given type. */
export function refId(ref: Reference | undefined, type: string): string | null {
  const value = ref?.reference;
  if (!value?.startsWith(`${type}/`)) {
    return null;
  }
  return value.slice(type.length + 1) || null;
}

function typeCoding(appointment: Appointment): Coding | undefined {
  const codings = appointment.appointmentType?.coding ?? [];
  return codings.find((c) => c.system === APPOINTMENT_TYPES_SYSTEM) ?? codings[0];
}

function actor(appointment: Appointment, type: string): Reference | undefined {
  return appointment.participant?.find((p) => p.actor?.reference?.startsWith(`${type}/`))?.actor;
}

const PROCEDURE_CODE_SET: ReadonlySet<string> = new Set(PREPROCEDURE_APPOINTMENT_CODES);

/** Retool `patients_by_appointments_medplum` transformer. One row per procedure appointment with a patient. */
export function extractProcedureAppointments(appointments: Appointment[]): ProcedureAppointment[] {
  const rows: ProcedureAppointment[] = [];
  for (const a of appointments) {
    const coding = typeCoding(a);
    const code = coding?.code;
    const patient = actor(a, 'Patient');
    const patientId = refId(patient, 'Patient');
    if (!a.id || !code || !PROCEDURE_CODE_SET.has(code) || !patientId) {
      continue;
    }
    const practitioner = actor(a, 'Practitioner');
    const location = actor(a, 'Location');
    rows.push({
      appointmentId: a.id,
      patientId,
      patientName: patient?.display ?? null,
      providerId: refId(practitioner, 'Practitioner'),
      providerName: practitioner?.display ?? null,
      locationId: refId(location, 'Location'),
      locationName: location?.display ?? null,
      start: a.start ?? null,
      typeCode: code,
      typeDisplay: PROCEDURE_DISPLAY[code] ?? coding?.display ?? code,
      comment: a.comment ?? null,
    });
  }
  return rows;
}

/** Retool `healthieFallback`: Healthie-migrated tasks carry the note as `"<label>: <text>"`. */
function healthieNote(task: Task): string | null {
  const content = healthieData(task.extension)?.content;
  if (typeof content !== 'string') {
    return null;
  }
  const colon = content.indexOf(': ');
  return colon === -1 ? null : content.slice(colon + 2);
}

const TASK_CODE_SET: ReadonlySet<string> = new Set(PREPROCEDURE_TASK_CODES);

/** Latest-updated Task per (patient, pre-procedure code). */
export function summarizeTasks(tasks: Task[]): Record<string, TasksByCode> {
  const result: Record<string, TasksByCode> = {};
  for (const t of tasks) {
    const patientId = refId(t.for, 'Patient');
    const code = t.code?.coding?.[0]?.code;
    if (!t.id || !patientId || !code || !TASK_CODE_SET.has(code)) {
      continue;
    }
    const byCode = (result[patientId] ??= {});
    const existing = byCode[code as PreprocedureTaskCode];
    const lastUpdated = t.meta?.lastUpdated ?? null;
    if (existing && (existing.lastUpdated ?? '') >= (lastUpdated ?? '')) {
      continue;
    }
    byCode[code as PreprocedureTaskCode] = {
      id: t.id,
      status: t.status,
      lastUpdated,
      note: t.note?.[0]?.text ?? healthieNote(t),
    };
  }
  return result;
}

const MILESTONE_SET: ReadonlySet<string> = new Set(MILESTONE_CODES);

/** Retool `milestoneAppointments_medplum` transformer. */
export function summarizeMilestones(appointments: Appointment[]): Record<string, Milestones> {
  const result: Record<string, Milestones> = {};
  for (const a of appointments) {
    const code = (a.appointmentType?.coding ?? []).find((c) => c.code && MILESTONE_SET.has(c.code))?.code as
      | MilestoneCode
      | undefined;
    if (!code || !a.start) {
      continue;
    }
    const patientIds = (a.participant ?? []).map((p) => refId(p.actor, 'Patient')).filter((id): id is string => !!id);
    for (const patientId of patientIds) {
      const milestones = (result[patientId] ??= {});
      const existing = milestones[code];
      const earliest = EARLIEST_WINS_MILESTONES.has(code);
      if (!existing || (earliest ? a.start < existing : a.start > existing)) {
        milestones[code] = a.start;
      }
    }
  }
  return result;
}

function practitionerName(p: Practitioner): string | null {
  const name = p.name?.[0];
  if (!name) {
    return null;
  }
  return `${name.given?.[0] ?? ''} ${name.family ?? ''}`.trim() || null;
}

/** Retool careTeam_by_patients + pn_roles + pn_practitioners: first CareTeam member holding a PN role. */
export function summarizeNavigators(
  careTeams: CareTeam[],
  pnRoles: PractitionerRole[],
  practitioners: Practitioner[]
): Record<string, string> {
  const pnIds = new Set(pnRoles.map((r) => refId(r.practitioner, 'Practitioner')).filter(Boolean));
  const names = new Map(practitioners.map((p) => [p.id, practitionerName(p)]));
  const result: Record<string, string> = {};
  for (const team of careTeams) {
    const patientId = refId(team.subject, 'Patient');
    if (!patientId || result[patientId]) {
      continue;
    }
    for (const participant of team.participant ?? []) {
      const id = refId(participant.member, 'Practitioner');
      const name = id && pnIds.has(id) ? names.get(id) : null;
      if (name) {
        result[patientId] = name;
        break;
      }
    }
  }
  return result;
}

function healthieData(extensions: Extension[] | undefined): Record<string, unknown> | null {
  const raw = extensions?.find((e) => e.url === HEALTHIE_DATA_EXTENSION)?.valueString;
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Retool `getPrescriptions_medplum` transformer. */
export function summarizePrescriptions(requests: MedicationRequest[]): Record<string, RxSummary> {
  const result: Record<string, RxSummary> = {};
  for (const r of requests) {
    const patientId = refId(r.subject, 'Patient');
    if (!patientId || r.status === 'cancelled') {
      continue;
    }
    const summary = (result[patientId] ??= { count: 0, eRxSentCount: 0, lastWrittenAt: null });
    summary.count += 1;
    const healthieStatus = healthieData(r.extension)?.status;
    const doseSpotStatus = r.extension?.find((e) => e.url === DOSESPOT_STATUS_EXTENSION)?.valueCode;
    if (healthieStatus === 'PharmacyVerified' || doseSpotStatus === 'eRxSent') {
      summary.eRxSentCount += 1;
    }
    if (r.authoredOn && (!summary.lastWrittenAt || r.authoredOn > summary.lastWrittenAt)) {
      summary.lastWrittenAt = r.authoredOn;
    }
  }
  return result;
}

/** Retool `labOrders_medplum` transformer. */
export function summarizeLabOrders(requests: ServiceRequest[]): Record<string, LabSummary> {
  const acc: Record<string, LabSummary & { statuses: Set<string> }> = {};
  for (const r of requests) {
    const patientId = refId(r.subject, 'Patient');
    if (!patientId) {
      continue;
    }
    const s = (acc[patientId] ??= {
      count: 0,
      completedCount: 0,
      aggregatedStatus: null,
      firstOrdererName: null,
      firstCreatedDate: null,
      lastUpdatedDate: null,
      labs: [],
      statuses: new Set(),
    });
    s.count += 1;
    if (r.status) {
      s.statuses.add(r.status);
      if (r.status === 'completed') {
        s.completedCount += 1;
      }
    }
    if (r.authoredOn && (!s.firstCreatedDate || r.authoredOn < s.firstCreatedDate)) {
      s.firstCreatedDate = r.authoredOn;
      s.firstOrdererName = r.requester?.display ?? null;
    }
    const updated = r.meta?.lastUpdated;
    if (updated && (!s.lastUpdatedDate || updated > s.lastUpdatedDate)) {
      s.lastUpdatedDate = updated;
    }
    const lab = r.code?.text ?? r.code?.coding?.[0]?.display;
    if (lab && !s.labs.includes(lab)) {
      s.labs.push(lab);
    }
  }
  const result: Record<string, LabSummary> = {};
  for (const [patientId, { statuses, ...s }] of Object.entries(acc)) {
    const list = [...statuses];
    if (list.length === 1) {
      s.aggregatedStatus = list[0] === 'active' ? 'pending' : list[0];
    } else if (list.includes('completed')) {
      s.aggregatedStatus = 'partially_completed';
    } else if (list.length > 1) {
      s.aggregatedStatus = list.sort().join(', ');
    }
    result[patientId] = s;
  }
  return result;
}

interface HealthieFormData {
  custom_module_form?: { name?: string };
  filler?: { first_name?: string; last_name?: string };
}

/**
 * Retool getLabsReviewed_* + getLabsReviewedNative_*: completed lab-review questionnaires (native or
 * Healthie-migrated), overridden by completed app-lab-review Tasks whose output QR supplies the author.
 */
export function summarizeLabsReviewed(
  responses: QuestionnaireResponse[],
  reviewTasks: Task[],
  reviewTaskResponses: QuestionnaireResponse[]
): Record<string, LabsReviewed> {
  const result: Record<string, LabsReviewed> = {};
  for (const r of responses) {
    const patientId = refId(r.subject, 'Patient');
    if (!patientId) {
      continue;
    }
    if (r.questionnaire && LAB_REVIEW_QUESTIONNAIRES.has(r.questionnaire)) {
      result[patientId] = { date: r.authored ?? null, author: r.source?.display ?? null };
      continue;
    }
    const parsed = healthieData(r.extension) as HealthieFormData | null;
    const formName = parsed?.custom_module_form?.name;
    if (formName && HEALTHIE_LAB_REVIEW_FORM_NAMES.has(formName)) {
      result[patientId] = {
        date: r.authored ?? null,
        author: parsed?.filler ? `${parsed.filler.first_name ?? ''} ${parsed.filler.last_name ?? ''}`.trim() : null,
      };
    }
  }
  const qrAuthors = new Map(
    reviewTaskResponses.map((qr) => [`QuestionnaireResponse/${qr.id}`, qr.source?.display ?? null])
  );
  for (const t of reviewTasks) {
    const patientId = refId(t.for, 'Patient');
    const qrRef = t.output?.find((o) => o.type?.text === 'QuestionnaireResponse')?.valueReference?.reference;
    if (!patientId || !qrRef) {
      continue;
    }
    result[patientId] = { date: t.authoredOn ?? null, author: qrAuthors.get(qrRef) ?? null };
  }
  return result;
}

/** Earliest active scale Device per patient; `meta.lastUpdated` stands in for creation time. */
export function summarizeScales(devices: Device[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const d of devices) {
    const patientId = refId(d.patient, 'Patient');
    const at = d.meta?.lastUpdated;
    if (!patientId || !at) {
      continue;
    }
    if (!result[patientId] || at < result[patientId]) {
      result[patientId] = at;
    }
  }
  return result;
}
